import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../components/auth/AuthContext";
import { HeaderBar } from "../components/layout/TopBar";
import NavBar from "../components/layout/NavBar";
import { RecycleArt } from "../components/adminSurface/heroArt";
import {
  getDatabases, getLifecycleRules, getDispositions, previewDisposition,
  proposeDisposition, approveDisposition, rejectDisposition, executeDisposition,
  getDispositionCertificate,
} from "../api/adminApi";

const dbId = (d) => d?.id ?? d?.Id ?? d?.dbInfoId ?? d?.DBInfoId;
const dbLabel = (d) =>
  d?.name ?? d?.Name ?? d?.displayName ?? d?.DisplayName
  ?? d?.dbDisplayName ?? d?.DbDisplayName ?? d?.dbName ?? d?.DbName ?? String(dbId(d));

function fmtDate(raw) {
  if (!raw) return "";
  try {
    const d = new Date(raw);
    if (isNaN(d)) return String(raw);
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  } catch { return String(raw); }
}

function fmtBytes(n) {
  const b = Number(n) || 0;
  if (b < 1024) return `${b} B`;
  const u = ["KB", "MB", "GB", "TB", "PB"];
  let v = b / 1024, i = 0;
  while (v >= 1024 && i < u.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(v >= 100 ? 0 : 1)} ${u[i]}`;
}

// Scoped styling — shares the content-governance "AI / newgen" visual language
// (indigo→violet gradient, soft floating cards, focus rings, segmented controls)
// with the Legal Holds surface, kept self-contained so no global CSS is touched.
const DSP_STYLES = `
.dsp-hero{ position:relative; overflow:hidden; border-radius:18px; padding:24px 28px; margin-bottom:20px;
  background:linear-gradient(135deg,#312e81 0%,#4338ca 46%,#6d28d9 100%);
  box-shadow:0 14px 38px -10px rgba(67,56,202,.55); display:flex; align-items:center; gap:20px; }
.dsp-hero__glow{ position:absolute; inset:0; pointer-events:none;
  background:radial-gradient(ellipse at 80% 25%, rgba(255,255,255,.16) 0%, transparent 55%); }
.dsp-hero__logo{ position:relative; flex:0 0 auto; display:flex; align-items:center; pointer-events:none; }
.dsp-hero__logo svg{ display:block; height:82px; width:auto; }
.dsp-hero__body{ position:relative; flex:1; min-width:0; }
.dsp-hero__title{ margin:0; font-size:23px; font-weight:800; color:#fff; letter-spacing:-.01em; }
.dsp-hero__sub{ margin:5px 0 0; font-size:13px; color:rgba(255,255,255,.78); }
.dsp-hero__stat{ position:relative; display:flex; flex-direction:column; align-items:flex-end; padding-right:4px; }
.dsp-hero__stat b{ font-size:28px; font-weight:800; color:#fff; line-height:1; }
.dsp-hero__stat span{ display:inline-flex; align-items:center; gap:6px; font-size:11px; text-transform:uppercase;
  letter-spacing:.08em; color:rgba(255,255,255,.72); margin-top:6px; }
.dsp-refresh{ position:relative; appearance:none; width:40px; height:40px; border-radius:11px; flex:0 0 auto;
  border:1px solid rgba(255,255,255,.26); background:rgba(255,255,255,.12); color:#fff; font-size:17px; cursor:pointer;
  transition:background .15s, transform .12s; display:flex; align-items:center; justify-content:center; }
.dsp-refresh:hover{ background:rgba(255,255,255,.24); }
.dsp-refresh:active{ transform:translateY(1px); }
.dsp-refresh:disabled{ opacity:.6; cursor:not-allowed; }

.dsp-card{ background:#fff; border:1px solid #eef0f5; border-radius:16px; padding:20px 22px; margin-bottom:16px;
  box-shadow:0 10px 34px -14px rgba(15,23,42,.14); }
.dsp-grid{ display:flex; gap:14px; flex-wrap:wrap; align-items:flex-end; }
.dsp-field{ display:flex; flex-direction:column; min-width:0; }
.dsp-lbl{ font-size:10.5px; font-weight:700; color:#64748b; text-transform:uppercase; letter-spacing:.07em; margin-bottom:6px; }
.dsp-input{ width:100%; box-sizing:border-box; padding:10px 12px; border-radius:10px; border:1px solid #e2e8f0;
  font-size:13.5px; font-family:inherit; background:#fff; color:#0f172a; transition:border-color .15s, box-shadow .15s; }
.dsp-input:hover{ border-color:#cbd5e1; }
.dsp-input:focus{ outline:none; border-color:#7c3aed; box-shadow:0 0 0 3px rgba(124,58,237,.15); }
.dsp-input::placeholder{ color:#94a3b8; }
textarea.dsp-input{ resize:vertical; min-height:60px; line-height:1.5; }

.dsp-seg{ display:inline-flex; background:#f1f5f9; border:1px solid #e6eaf1; border-radius:11px; padding:3px; gap:2px; }
.dsp-seg button{ appearance:none; border:none; background:transparent; color:#475569; font-size:12.5px; font-weight:600;
  padding:8px 14px; border-radius:8px; cursor:pointer; transition:all .15s; white-space:nowrap; }
.dsp-seg button:hover{ color:#1e293b; }
.dsp-seg button.on{ background:#fff; color:#6d28d9; box-shadow:0 1px 3px rgba(15,23,42,.14); }

.dsp-cta{ appearance:none; border:none; cursor:pointer; color:#fff; font-weight:700; font-size:14px; padding:11px 24px;
  border-radius:12px; background:linear-gradient(135deg,#4338ca,#6d28d9 55%,#7c3aed);
  box-shadow:0 8px 20px -4px rgba(109,40,217,.42); transition:filter .15s, box-shadow .15s, transform .12s;
  display:inline-flex; align-items:center; gap:8px; }
.dsp-cta:hover{ filter:brightness(1.06); box-shadow:0 10px 26px -4px rgba(109,40,217,.5); }
.dsp-cta:active{ transform:translateY(1px); }
.dsp-cta:disabled{ opacity:.6; cursor:not-allowed; filter:none; box-shadow:none; }
.dsp-cta.ghost{ background:#f1f5f9; color:#4338ca; box-shadow:none; border:1px solid #e2e8f0; }
.dsp-cta.ghost:hover{ background:#eae7fb; }
.dsp-cta.danger{ background:linear-gradient(135deg,#b91c1c,#dc2626 60%,#ef4444); box-shadow:0 8px 20px -4px rgba(220,38,38,.42); }

.dsp-tablecard{ background:#fff; border:1px solid #eef0f5; border-radius:16px; overflow:hidden;
  box-shadow:0 10px 34px -14px rgba(15,23,42,.12); }
.dsp-row td{ transition:background .12s; }
.dsp-row:hover td{ background:#faf7ff !important; }

.dsp-chip{ display:inline-block; padding:3px 9px; border-radius:7px; font-size:11px; font-weight:600;
  background:#f4f2fb; color:#5b21b6; border:1px solid #ece7fa; white-space:nowrap; }
.dsp-status{ display:inline-flex; align-items:center; gap:6px; padding:3px 10px 3px 8px; border-radius:999px;
  font-size:11px; font-weight:700; letter-spacing:.02em; }
.dsp-status__dot{ width:6px; height:6px; border-radius:50%; background:currentColor; }
.dsp-status--proposed{ background:#fef9c3; color:#a16207; }
.dsp-status--approved{ background:#dbeafe; color:#1d4ed8; }
.dsp-status--executed{ background:#dcfce7; color:#15803d; }
.dsp-status--rejected{ background:#f1f5f9; color:#64748b; }
.dsp-status--failed{ background:#fee2e2; color:#b91c1c; }
.dsp-status--proposed .dsp-status__dot,.dsp-status--approved .dsp-status__dot{ animation:dsp-pulse 1.9s ease-in-out infinite; }
@keyframes dsp-pulse{ 0%,100%{opacity:1; transform:scale(1);} 50%{opacity:.35; transform:scale(.8);} }

.dsp-empty{ padding:64px 40px; text-align:center; }
.dsp-empty__ring{ width:76px; height:76px; margin:0 auto 16px; border-radius:22px; display:flex; align-items:center;
  justify-content:center; font-size:34px; background:linear-gradient(135deg,#eef2ff,#f5f0ff); border:1px solid #eae7fb; }

@keyframes dsp-slide{ from{opacity:0; transform:translateY(-4px);} to{opacity:1; transform:none;} }
.dsp-slide{ animation:dsp-slide .18s ease; }
.dsp-act{ background:linear-gradient(180deg,#fbfaff,#f6f2ff); }
.dsp-danger{ background:linear-gradient(180deg,#fffdf5,#fff7f7); }

.dsp-dd{ position:relative; }
.dsp-dd__btn{ appearance:none; display:inline-flex; align-items:center; gap:9px; width:100%; box-sizing:border-box;
  padding:10px 12px; border-radius:10px; border:1px solid #e2e8f0; background:#fff; color:#0f172a; font-size:13.5px;
  font-family:inherit; cursor:pointer; transition:border-color .15s, box-shadow .15s; }
.dsp-dd__btn:hover{ border-color:#cbd5e1; }
.dsp-dd__btn.open{ border-color:#7c3aed; box-shadow:0 0 0 3px rgba(124,58,237,.15); }
.dsp-dd__btn:disabled{ opacity:.55; cursor:not-allowed; }
.dsp-dd__ico{ font-size:14px; opacity:.75; flex:0 0 auto; line-height:1; }
.dsp-dd__lbl{ flex:1; text-align:left; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.dsp-dd__lbl.ph{ color:#94a3b8; }
.dsp-dd__chev{ flex:0 0 auto; font-size:11px; color:#94a3b8; transition:transform .15s, color .15s; }
.dsp-dd__btn.open .dsp-dd__chev{ transform:rotate(180deg); color:#7c3aed; }
.dsp-dd__menu{ position:absolute; top:calc(100% + 6px); left:0; min-width:100%; z-index:300; box-sizing:border-box;
  background:#fff; border:1px solid #eef0f5; border-radius:12px; box-shadow:0 18px 44px -12px rgba(15,23,42,.3);
  padding:6px; max-height:288px; overflow-y:auto; animation:dsp-slide .16s ease; }
.dsp-dd__opt{ display:flex; align-items:center; gap:10px; padding:9px 11px; border-radius:9px; font-size:13.5px;
  color:#334155; cursor:pointer; white-space:nowrap; transition:background .1s; }
.dsp-dd__opt:hover{ background:#f6f2ff; color:#1e293b; }
.dsp-dd__opt.sel{ color:#6d28d9; font-weight:600; background:#f6f2ff; }
.dsp-dd__dot{ width:8px; height:8px; border-radius:3px; background:#cbd5e1; flex:0 0 auto; transition:background .12s; }
.dsp-dd__opt:hover .dsp-dd__dot{ background:#a78bfa; }
.dsp-dd__opt.sel .dsp-dd__dot{ background:#7c3aed; }
.dsp-dd__check{ margin-left:auto; color:#7c3aed; font-size:13px; opacity:0; }
.dsp-dd__opt.sel .dsp-dd__check{ opacity:1; }

.dsp-switch{ position:relative; display:inline-flex; align-items:center; gap:10px; cursor:pointer; user-select:none;
  font-size:13px; font-weight:500; color:#334155; }
.dsp-switch input{ position:absolute; opacity:0; width:0; height:0; }
.dsp-switch__track{ width:42px; height:24px; border-radius:999px; background:#cbd5e1; position:relative;
  transition:background .18s, box-shadow .15s; flex:0 0 auto; }
.dsp-switch__track::after{ content:""; position:absolute; top:2px; left:2px; width:20px; height:20px; border-radius:50%;
  background:#fff; box-shadow:0 1px 3px rgba(15,23,42,.32); transition:transform .18s; }
.dsp-switch input:checked + .dsp-switch__track{ background:linear-gradient(135deg,#6d28d9,#7c3aed); }
.dsp-switch input:checked + .dsp-switch__track::after{ transform:translateX(18px); }
.dsp-switch input:focus-visible + .dsp-switch__track{ box-shadow:0 0 0 3px rgba(124,58,237,.28); }

.dsp-toolbar{ display:flex; gap:14px; align-items:center; flex-wrap:wrap; background:#fff; border:1px solid #eef0f5;
  border-radius:14px; padding:12px 14px; margin-bottom:14px; box-shadow:0 8px 26px -16px rgba(15,23,42,.16); }
.dsp-toolbar .dsp-dd{ min-width:200px; }
.dsp-count{ margin-left:auto; display:inline-flex; align-items:center; gap:6px; font-size:12px; color:#5b21b6;
  background:#f4f2fb; border:1px solid #ece7fa; padding:6px 12px; border-radius:999px; font-weight:700; }
.dsp-preview{ margin-top:14px; padding:12px 16px; border-radius:12px; font-size:13px; display:flex; align-items:center; gap:10px; }
.dsp-preview.ok{ background:#f5f3ff; border:1px solid #e9e5fb; color:#5b21b6; }
.dsp-preview.warn{ background:#fffbeb; border:1px solid #fde68a; color:#92400e; }
.dsp-cert{ font-family:ui-monospace, monospace; font-size:12.5px; color:#334155; }
.dsp-cert dt{ font-size:10.5px; font-weight:700; color:#64748b; text-transform:uppercase; letter-spacing:.06em; }
.dsp-cert dd{ margin:2px 0 12px; }
.dsp-verify{ display:inline-flex; align-items:center; gap:7px; padding:5px 12px; border-radius:999px; font-size:12px; font-weight:700; }
.dsp-verify.ok{ background:#dcfce7; color:#15803d; }
.dsp-verify.bad{ background:#fee2e2; color:#b91c1c; }
`;

function Dropdown({ value, onChange, options, placeholder, icon, disabled }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  const sel = options.find((o) => String(o.value) === String(value));
  return (
    <div className="dsp-dd" ref={ref}>
      <button type="button" className={`dsp-dd__btn${open ? " open" : ""}`} disabled={disabled}
        onClick={() => setOpen((o) => !o)}>
        {icon && <span className="dsp-dd__ico">{icon}</span>}
        <span className={`dsp-dd__lbl${sel ? "" : " ph"}`}>{sel ? sel.label : placeholder}</span>
        <span className="dsp-dd__chev">▾</span>
      </button>
      {open && (
        <div className="dsp-dd__menu">
          {options.map((o) => {
            const on = String(o.value) === String(value);
            return (
              <div key={o.value} className={`dsp-dd__opt${on ? " sel" : ""}`}
                onClick={() => { onChange(o.value); setOpen(false); }}>
                <span className="dsp-dd__dot" />
                <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{o.label}</span>
                <span className="dsp-dd__check">✓</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function DispositionPage() {
  const { t } = useTranslation();
  const { token } = useAuth();

  const [databases, setDatabases] = useState([]);
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // composer
  const [name, setName] = useState("");
  const [formDbId, setFormDbId] = useState("");
  const [source, setSource] = useState("rule"); // "rule" | "adhoc"
  const [rules, setRules] = useState([]);
  const [ruleId, setRuleId] = useState("");
  const [ageValue, setAgeValue] = useState("");
  const [ageUnit, setAgeUnit] = useState("Years");
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [proposing, setProposing] = useState(false);

  // filters
  const [filterDbId, setFilterDbId] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [openOnly, setOpenOnly] = useState(false);

  // inline actions
  const [actId, setActId] = useState(null);        // batch id being acted on
  const [actKind, setActKind] = useState(null);    // "approve" | "reject" | "execute" | "cert"
  const [actText, setActText] = useState("");
  const [acting, setActing] = useState(false);
  const [cert, setCert] = useState(null);

  useEffect(() => {
    if (!token) return;
    getDatabases(token)
      .then((data) => setDatabases(Array.isArray(data) ? data : (data?.databases ?? data?.Databases ?? [])))
      .catch(() => {});
  }, [token]);

  // load physical-delete rules for the selected database (rule-derived source)
  useEffect(() => {
    setRuleId(""); setPreview(null);
    if (!token || !formDbId || source !== "rule") { setRules([]); return; }
    getLifecycleRules(token, Number(formDbId))
      .then((data) => {
        const all = Array.isArray(data) ? data : (data?.rules ?? []);
        setRules(all.filter((r) =>
          String(r.action ?? r.Action).toLowerCase() === "delete" &&
          String(r.deletionDepth ?? r.DeletionDepth).toLowerCase() === "physical"));
      })
      .catch(() => setRules([]));
  }, [token, formDbId, source]);

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const data = await getDispositions(token, {
        databaseId: filterDbId || undefined,
        status: filterStatus || undefined,
        openOnly: openOnly || undefined,
        take: 100,
      });
      setBatches(Array.isArray(data?.items) ? data.items : (Array.isArray(data) ? data : []));
    } catch (e) {
      setError(e?.message || t("disposition.loadFailed"));
    } finally {
      setLoading(false);
    }
  }, [token, filterDbId, filterStatus, openOnly, t]);

  useEffect(() => { load(); }, [load]);

  const buildBody = () => {
    if (source === "adhoc") {
      return {
        source: "AdHoc", name: name.trim(), databaseId: Number(formDbId),
        dateBasis: "DocumentDate", maxAgeValue: Number(ageValue), maxAgeUnit: ageUnit,
      };
    }
    return { source: "Rule", name: name.trim(), ruleId: Number(ruleId) };
  };

  const canSubmit = name.trim() && formDbId &&
    (source === "rule" ? !!ruleId : Number(ageValue) > 0);

  const runPreview = async () => {
    if (!canSubmit || previewing) return;
    setPreviewing(true); setError(""); setPreview(null);
    try {
      const p = await previewDisposition(token, buildBody());
      setPreview(p);
    } catch (e) {
      setError(e?.message || String(e));
    } finally {
      setPreviewing(false);
    }
  };

  const propose = async () => {
    if (proposing) return;
    if (!canSubmit) { setError(t("disposition.nameDbRequired")); return; }
    setProposing(true); setError("");
    try {
      await proposeDisposition(token, buildBody());
      setName(""); setRuleId(""); setAgeValue(""); setPreview(null);
      await load();
    } catch (e) {
      setError(t("disposition.actionFailed", { error: e?.message || String(e) }));
    } finally {
      setProposing(false);
    }
  };

  const startAct = (id, kind) => { setActId(id); setActKind(kind); setActText(""); setCert(null); };
  const cancelAct = () => { setActId(null); setActKind(null); setActText(""); setCert(null); };

  const confirmAct = async (b) => {
    if (acting) return;
    setActing(true); setError("");
    try {
      if (actKind === "approve") await approveDisposition(token, b.id, actText.trim() || null);
      else if (actKind === "reject") await rejectDisposition(token, b.id, actText.trim() || null);
      else if (actKind === "execute") await executeDisposition(token, b.id);
      cancelAct();
      await load();
    } catch (e) {
      setError(t("disposition.actionFailed", { error: e?.message || String(e) }));
    } finally {
      setActing(false);
    }
  };

  const showCert = async (b) => {
    setActId(b.id); setActKind("cert"); setCert(null); setError("");
    try {
      const c = await getDispositionCertificate(token, b.id);
      setCert(c);
    } catch (e) {
      setError(t("disposition.actionFailed", { error: e?.message || String(e) }));
    }
  };

  const activeDbs = useMemo(() => databases.filter((d) => !(d.isClosed || d.IsClosed)), [databases]);
  const openCount = useMemo(() => batches.filter((b) => b.status === "Proposed" || b.status === "Approved").length, [batches]);

  const statusLabel = (s) => t(`disposition.status${s}`, { defaultValue: s });
  const statusClass = (s) => `dsp-status dsp-status--${String(s).toLowerCase()}`;

  const dbOpts = activeDbs.map((d) => ({ value: String(dbId(d)), label: dbLabel(d) }));
  const statusOpts = [
    { value: "", label: t("disposition.allStatuses") },
    ...["Proposed", "Approved", "Executed", "Rejected", "Failed"].map((s) => ({ value: s, label: statusLabel(s) })),
  ];

  return (
    <div className="app-root">
      <div className="nav-strip"><NavBar /></div>
      <HeaderBar />
      <style>{DSP_STYLES}</style>

      <div className="page-container" style={{ paddingBottom: 40 }}>
        {/* ── Gradient hero ── */}
        <div className="dsp-hero">
          <div className="dsp-hero__glow" />
          <div className="dsp-hero__logo"><RecycleArt /></div>
          <div className="dsp-hero__body">
            <h1 className="dsp-hero__title">{t("disposition.title")}</h1>
            <p className="dsp-hero__sub">{t("disposition.subtitle")}</p>
          </div>
          <div className="dsp-hero__stat">
            <b>{openCount}</b>
            <span><i className="dsp-status__dot" style={{ background: "#fbbf24" }} /> {t("disposition.pending")}</span>
          </div>
          <button className="dsp-refresh" onClick={load} disabled={loading} title={t("disposition.refresh")}>
            {loading ? "⟳" : "↺"}
          </button>
        </div>

        {/* ── Composer ── */}
        <div className="dsp-card">
          <div className="dsp-grid">
            <div className="dsp-field" style={{ flex: "2 1 220px" }}>
              <label className="dsp-lbl">{t("disposition.name")}</label>
              <input className="dsp-input" type="text" placeholder={t("disposition.namePlaceholder")}
                value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <div className="dsp-field" style={{ flex: "1 1 200px" }}>
              <label className="dsp-lbl">{t("disposition.database")}</label>
              <Dropdown icon="🗄" value={formDbId} onChange={setFormDbId}
                placeholder={t("disposition.selectDatabase")} options={dbOpts} />
            </div>
            <div className="dsp-field" style={{ flex: "0 0 auto" }}>
              <label className="dsp-lbl">{t("disposition.source")}</label>
              <div className="dsp-seg" role="tablist">
                <button type="button" className={source === "rule" ? "on" : ""}
                  onClick={() => { setSource("rule"); setPreview(null); }}>{t("disposition.fromRule")}</button>
                <button type="button" className={source === "adhoc" ? "on" : ""}
                  onClick={() => { setSource("adhoc"); setPreview(null); }}>{t("disposition.adHoc")}</button>
              </div>
            </div>
          </div>

          {source === "rule" ? (
            <div className="dsp-grid dsp-slide" style={{ marginTop: 14 }}>
              <div className="dsp-field" style={{ flex: "1 1 320px" }}>
                <label className="dsp-lbl">{t("disposition.rule")}</label>
                <Dropdown icon="📋" value={ruleId} onChange={(v) => { setRuleId(v); setPreview(null); }}
                  disabled={!formDbId || rules.length === 0}
                  placeholder={formDbId && rules.length === 0 ? t("disposition.noRules") : t("disposition.selectRule")}
                  options={rules.map((r) => ({ value: String(r.id ?? r.Id), label: `${r.name ?? r.Name} · ${r.maxAgeValue ?? r.MaxAgeValue} ${r.maxAgeUnit ?? r.MaxAgeUnit}` }))} />
              </div>
            </div>
          ) : (
            <div className="dsp-grid dsp-slide" style={{ marginTop: 14 }}>
              <div className="dsp-field" style={{ flex: "0 0 auto" }}>
                <label className="dsp-lbl">{t("disposition.maxAge")}</label>
                <input className="dsp-input" type="number" min={1} style={{ width: 120 }}
                  value={ageValue} onChange={(e) => { setAgeValue(e.target.value); setPreview(null); }} />
              </div>
              <div className="dsp-field" style={{ flex: "0 0 auto" }}>
                <label className="dsp-lbl">&nbsp;</label>
                <div className="dsp-seg">
                  {["Days", "Months", "Years"].map((u) => (
                    <button key={u} type="button" className={ageUnit === u ? "on" : ""}
                      onClick={() => { setAgeUnit(u); setPreview(null); }}>{t(`disposition.unit${u}`)}</button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {preview && (
            <div className={`dsp-preview ${preview.blocked ? "warn" : "ok"} dsp-slide`}>
              <span style={{ fontSize: 16 }}>{preview.blocked ? "⚠" : "🔍"}</span>
              {preview.blocked
                ? t("disposition.previewBlocked", { reason: preview.blockReason || "" })
                : (Number(preview.candidateDocs) > 0
                  ? t("disposition.previewResult", { docs: preview.candidateDocs, bytes: fmtBytes(preview.estimatedBytes), cutoff: preview.cutoffSummary || "" })
                  : t("disposition.previewNone"))}
            </div>
          )}

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 14 }}>
            <button className="dsp-cta ghost" onClick={runPreview} disabled={!canSubmit || previewing}>
              🔍 {previewing ? t("disposition.previewing") : t("disposition.preview")}
            </button>
            <button className="dsp-cta" onClick={propose} disabled={!canSubmit || proposing}>
              ♻️ {proposing ? t("disposition.proposing") : t("disposition.propose")}
            </button>
          </div>
        </div>

        {/* ── Filters toolbar ── */}
        <div className="dsp-toolbar">
          <Dropdown icon="🗄" value={filterDbId} onChange={setFilterDbId}
            placeholder={t("disposition.allDatabases")}
            options={[{ value: "", label: t("disposition.allDatabases") }, ...dbOpts]} />
          <Dropdown icon="🏷" value={filterStatus} onChange={setFilterStatus}
            placeholder={t("disposition.allStatuses")} options={statusOpts} />
          <label className="dsp-switch">
            <input type="checkbox" checked={openOnly} onChange={(e) => setOpenOnly(e.target.checked)} />
            <span className="dsp-switch__track" />
            {t("disposition.openOnly")}
          </label>
          {!loading && <span className="dsp-count">♻️ {batches.length}</span>}
        </div>

        {/* ── Error ── */}
        {error && (
          <div style={{ color: "#b91c1c", background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 12,
            padding: "11px 16px", marginBottom: 12, fontSize: 13, display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 16 }}>⚠</span> {error}
          </div>
        )}

        {/* ── Batches table ── */}
        <div className="dsp-tablecard" style={{ overflowX: "auto" }}>
          {batches.length === 0 && !loading ? (
            <div className="dsp-empty">
              <div className="dsp-empty__ring">♻️</div>
              <div style={{ fontWeight: 700, fontSize: 16, color: "#0f172a" }}>{t("disposition.noBatches")}</div>
              <div style={{ fontSize: 13, color: "#64748b", marginTop: 6 }}>{t("disposition.noBatchesHint")}</div>
            </div>
          ) : (
            <table className="logs-table" style={{ minWidth: 940 }}>
              <thead>
                <tr>
                  <th style={{ paddingLeft: 18 }}>{t("disposition.colName")}</th>
                  <th style={{ width: 140 }}>{t("disposition.colDatabase")}</th>
                  <th style={{ width: 150 }}>{t("disposition.colCandidates")}</th>
                  <th style={{ width: 120 }}>{t("disposition.colStatus")}</th>
                  <th style={{ width: 180 }}>{t("disposition.colProposed")}</th>
                  <th style={{ width: 200 }}></th>
                </tr>
              </thead>
              <tbody>
                {batches.map((b) => (
                  <React.Fragment key={b.id}>
                    <tr className="dsp-row">
                      <td style={{ paddingLeft: 18, fontSize: 13, color: "#0f172a", fontWeight: 500 }}>
                        {b.name}
                        {b.trigger === "Scheduled" && <span className="dsp-chip" style={{ marginLeft: 8 }}>{t("disposition.scheduled")}</span>}
                        {b.requireDualApproval && <span className="dsp-chip" style={{ marginLeft: 8, background: "#eef2ff", color: "#4338ca" }}>👥 {t("disposition.dualApproval")}</span>}
                        {b.cutoffSummary && <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 3, lineHeight: 1.4 }}>{b.cutoffSummary}</div>}
                      </td>
                      <td><span className="dsp-chip">{b.dbName}</span></td>
                      <td style={{ fontSize: 12.5, color: "#334155" }}>
                        {b.status === "Executed"
                          ? <b style={{ color: "#15803d" }}>{t("disposition.disposedSummary", { docs: b.docsDisposed ?? 0, bytes: fmtBytes(b.bytesReclaimed) })}</b>
                          : `${b.candidateDocs ?? 0} ${t("disposition.docsUnit")} · ${fmtBytes(b.estimatedBytes)}`}
                      </td>
                      <td>
                        <span className={statusClass(b.status)}>
                          <span className="dsp-status__dot" />{statusLabel(b.status)}
                        </span>
                      </td>
                      <td style={{ fontSize: 12, color: "#64748b" }}>
                        {fmtDate(b.proposedAt)}{b.proposedBy ? ` ${t("disposition.by", { user: b.proposedBy })}` : ""}
                      </td>
                      <td>
                        <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                          {b.status === "Proposed" && (
                            <>
                              <button className="link-btn" style={{ fontSize: 13, color: "#1d4ed8" }} onClick={() => startAct(b.id, "approve")}>{t("disposition.approve")}</button>
                              <button className="link-btn" style={{ fontSize: 13, color: "#64748b" }} onClick={() => startAct(b.id, "reject")}>{t("disposition.reject")}</button>
                            </>
                          )}
                          {b.status === "Approved" && (
                            <>
                              <button className="link-btn" style={{ fontSize: 13, color: "#b91c1c", fontWeight: 700 }} onClick={() => startAct(b.id, "execute")}>{t("disposition.execute")}</button>
                              <button className="link-btn" style={{ fontSize: 13, color: "#64748b" }} onClick={() => startAct(b.id, "reject")}>{t("disposition.reject")}</button>
                            </>
                          )}
                          {b.hasCertificate && (
                            <button className="link-btn" style={{ fontSize: 13, color: "#6d28d9" }} onClick={() => showCert(b)}>🏅 {t("disposition.certificate")}</button>
                          )}
                        </div>
                      </td>
                    </tr>

                    {/* inline approve / reject / execute */}
                    {actId === b.id && (actKind === "approve" || actKind === "reject" || actKind === "execute") && (
                      <tr className={actKind === "execute" ? "dsp-danger dsp-slide" : "dsp-act dsp-slide"}>
                        <td colSpan={6} style={{ padding: "14px 18px 16px 56px" }}>
                          <div style={{ fontSize: 13, color: actKind === "execute" ? "#991b1b" : "#4338ca", marginBottom: 10 }}>
                            {actKind === "approve" && t("disposition.approvePrompt")}
                            {actKind === "reject" && t("disposition.rejectPrompt")}
                            {actKind === "execute" && t("disposition.executePrompt")}
                          </div>
                          <div style={{ display: "flex", gap: 10, alignItems: "flex-end", flexWrap: "wrap" }}>
                            {actKind !== "execute" && (
                              <textarea className="dsp-input" rows={2} maxLength={1000}
                                placeholder={actKind === "approve" ? t("disposition.approveNotePlaceholder") : t("disposition.rejectReasonPlaceholder")}
                                value={actText} onChange={(e) => setActText(e.target.value)}
                                style={{ flex: "1 1 320px", minHeight: 48 }} />
                            )}
                            <button className={`dsp-cta${actKind === "execute" ? " danger" : ""}`} onClick={() => confirmAct(b)} disabled={acting}>
                              {actKind === "approve" && (acting ? "…" : t("disposition.confirmApprove"))}
                              {actKind === "reject" && (acting ? "…" : t("disposition.confirmReject"))}
                              {actKind === "execute" && (acting ? t("disposition.executing") : `🗑 ${t("disposition.confirmExecute")}`)}
                            </button>
                            <button className="link-btn" onClick={cancelAct} style={{ fontSize: 13 }}>{t("disposition.cancel")}</button>
                          </div>
                        </td>
                      </tr>
                    )}

                    {/* certificate view */}
                    {actId === b.id && actKind === "cert" && (
                      <tr className="dsp-act dsp-slide">
                        <td colSpan={6} style={{ padding: "16px 18px 18px 56px" }}>
                          {!cert ? <div style={{ fontSize: 13, color: "#64748b" }}>…</div> : (
                            <>
                              <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 12, flexWrap: "wrap" }}>
                                <span style={{ fontWeight: 800, fontSize: 15, color: "#0f172a" }}>🏅 {cert.certNumber}</span>
                                <span className={`dsp-verify ${cert.verified ? "ok" : "bad"}`}>
                                  {cert.verified ? `✓ ${t("disposition.certVerified")}` : `✗ ${t("disposition.certInvalid")}`}
                                </span>
                                {cert.payload?.algorithm && (
                                  <span className="dsp-chip" style={{ background: "#eef2ff", color: "#4338ca" }}>🔑 {cert.payload.algorithm}</span>
                                )}
                              </div>
                              <dl className="dsp-cert" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))", gap: "0 24px", margin: 0 }}>
                                <div><dt>{t("disposition.certDisposed")}</dt><dd>{(cert.payload?.docsDisposed ?? b.docsDisposed ?? 0)} {t("disposition.docsUnit")} · {fmtBytes(cert.payload?.bytesReclaimed ?? b.bytesReclaimed)}</dd></div>
                                <div><dt>{t("disposition.colProposed")}</dt><dd>{cert.payload?.proposedBy || "—"}</dd></div>
                                <div><dt>{t("disposition.approve")}</dt><dd>{cert.payload?.approvedBy || "—"}</dd></div>
                                <div><dt>{t("disposition.execute")}</dt><dd>{cert.payload?.executedBy || "—"}</dd></div>
                                <div style={{ gridColumn: "1 / -1" }}><dt>{t("disposition.colCutoff")}</dt><dd>{cert.payload?.policy || b.policyDescription || "—"}</dd></div>
                                <div style={{ gridColumn: "1 / -1" }}><dt>{t("disposition.certChain")}</dt><dd>#{cert.auditChainId ?? "—"} · {cert.payload?.signature ? `${cert.payload.signature.slice(0, 24)}…` : ""}{cert.payload?.keyId ? ` · ${cert.payload.keyId}` : ""}</dd></div>
                              </dl>
                              <div style={{ marginTop: 12 }}>
                                <button className="link-btn" onClick={cancelAct} style={{ fontSize: 13 }}>{t("disposition.certClose")}</button>
                              </div>
                            </>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
